import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { getToken } from "../../api/auth";
import { getUserById, editUser, getCompanies } from "../../api/user";
import {
  getData,
  getItems,
  getIUOM,
  sendData,
  syncStock,
  getCustomers,
} from "../../api/sync";
import { IoSettingsOutline } from "react-icons/io5";
import "./Profile.css";

const getUsernameFromToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.unique_name || payload.sub || null;
  } catch (err) {
    console.error("Failed to decode token:", err);
    return null;
  }
};

const Profile = () => {
  const token = getToken();
  const username = getUsernameFromToken();
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [userCompany, setUserCompany] = useState(null);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncPanelOpen, setIsSyncPanelOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    companyCode: "",
    name: "",
    email: "",
    phone: "",
    entryDate: "",
    roleId: "",
    password: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!username) {
        setError("Unable to retrieve username from token.");
        return;
      }
      try {
        // Fetch user data
        const userData = await getUserById(username);
        setUser(userData);
        setFormData({
          username: userData.username,
          companyCode: userData.companyCode,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          entryDate: userData.entryDate.split("T")[0],
          roleId: userData.roleId,
          password: "",
        });

        // Fetch company data
        const companyData = await getCompanies();
        setCompanies(companyData);

        // Find and set the user's specific company
        const matchingCompany = companyData.find(
          (company) => company.companyCode === userData.companyCode,
        );
        if (matchingCompany) {
          setUserCompany(matchingCompany);
        } else {
          setError("No matching company found for the user's company code.");
        }
      } catch (err) {
        setError("Failed to load profile or company data. Please try again.");
      }
    };

    if (token && username) {
      fetchData();
    }
  }, [token, username]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const userData = { ...formData };
      if (!userData.password) {
        delete userData.password;
      }
      await editUser(userData.username, userData);
      setUser({ ...user, ...userData });
      setIsModalOpen(false);
      setError(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.Password?.[0] ||
        "Failed to update profile. Please try again.";
      setError(errorMessage);
    }
  };

  const openEditModal = () => {
    setIsModalOpen(true);
    setError(null);
  };

  const handleSync = async (action) => {
    setIsSyncing(true);
    try {
      switch (action) {
        case "SalesHeader":
          await getData();
          break;
        case "Items":
          await getItems();
          break;
        case "IUOM":
          await getIUOM();
          break;
        case "Stock":
          await syncStock();
          break;
        case "Customers":
          await getCustomers();
          break;
        case "SendData":
          await sendData();
          break;
        default:
          throw new Error("Invalid sync action");
      }
      setError(null);
    } catch (err) {
      setError(`Failed to sync ${action}. Please try again.`);
    } finally {
      setIsSyncing(false);
      setIsConfirmOpen(false);
    }
  };

  const openConfirmModal = (action) => {
    setIsSyncPanelOpen(false);
    setConfirmAction(action);
    setIsConfirmOpen(true);
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!username) {
    return (
      <div className="error-message">Error: Username not found in token</div>
    );
  }

  if (!user) {
    return <div className="loading-message">Loading...</div>;
  }

  return (
    <div className="profile-page-container">
      <div className="profile-page-floating-bg profile-page-floating-bg-1"></div>
      <div className="profile-page-floating-bg profile-page-floating-bg-2"></div>
      <div className="profile-page-floating-bg profile-page-floating-bg-3"></div>
      <div className="profile-page-header">
        <div>
          <h2 className="profile-page-title">User Profile</h2>
          <p className="profile-page-subtitle">
            Manage your personal information
          </p>
        </div>
        <div className="profile-sync-panel-wrapper">
          <button
            className="profile-sync-settings-btn"
            onClick={() => setIsSyncPanelOpen(!isSyncPanelOpen)}
            disabled={isSyncing}
            title="Sync options"
          >
            <IoSettingsOutline size={24} />
          </button>
          {isSyncPanelOpen && (
            <div
              className="profile-sync-modal-overlay"
              onClick={() => setIsSyncPanelOpen(false)}
            >
              <div
                className="profile-sync-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="profile-sync-modal-header">
                  <h3>Sync Options</h3>
                  <button
                    className="profile-sync-modal-close"
                    onClick={() => setIsSyncPanelOpen(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="profile-sync-modal-body">
                  <button
                    className="profile-sync-btn profile-sync-btn-sales"
                    onClick={() => openConfirmModal("SalesHeader")}
                    disabled={isSyncing}
                  >
                    Sync SalesHeader
                  </button>
                  <button
                    className="profile-sync-btn profile-sync-btn-items"
                    onClick={() => openConfirmModal("Items")}
                    disabled={isSyncing}
                  >
                    Sync Items
                  </button>
                  <button
                    className="profile-sync-btn profile-sync-btn-stock"
                    onClick={() => openConfirmModal("Stock")}
                    disabled={isSyncing}
                  >
                    Sync Stock
                  </button>
                  <button
                    className="profile-sync-btn profile-sync-btn-customers"
                    onClick={() => openConfirmModal("Customers")}
                    disabled={isSyncing}
                  >
                    Sync Customers
                  </button>
                  {/* <button
                    className="profile-sync-btn profile-sync-btn-iuom"
                    onClick={() => openConfirmModal("IUOM")}
                    disabled={isSyncing}
                  >
                    Sync IUOM
                  </button> */}
                  <button
                    className="profile-sync-btn profile-sync-btn-send"
                    onClick={() => openConfirmModal("SendData")}
                    disabled={isSyncing}
                  >
                    Send Data
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {error && <div className="profile-error-message">{error}</div>}

      {/* Card Container for Side-by-Side Layout */}
      <div className="profile-card-container">
        {/* User Profile Card */}
        <div className="profile-user-card">
          <div className="profile-card-header">
            <div className="profile-initials">{user.name[0]}</div>
            <div className="profile-basic-info">
              <h3 className="profile-name">{user.name}</h3>
              <p className="profile-username">Username: {user.username}</p>
            </div>
          </div>
          <div className="profile-card-body">
            <div className="profile-detail-row">
              <span className="detail-label">Role ID:</span>
              <span className="detail-value">{user.roleId}</span>
            </div>
            <div className="profile-detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{user.email}</span>
            </div>
            <div className="profile-detail-row">
              <span className="detail-label">Phone:</span>
              <span className="detail-value">{user.phone}</span>
            </div>
            <div className="profile-detail-row">
              <span className="detail-label">Joined:</span>
              <span className="detail-value">
                {new Date(user.entryDate).toLocaleDateString()}
              </span>
            </div>
            <div className="profile-detail-row">
              <span className="detail-label">Company Code:</span>
              <span className="detail-value">{user.companyCode}</span>
            </div>
          </div>
          <div className="profile-card-actions">
            <button
              className="profile-glass-btn profile-btn-primary"
              onClick={openEditModal}
            >
              Edit Profile
            </button>
          </div>
        </div>

        {/* Company Info Card */}
        {userCompany && (
          <div className="profile-company-card">
            <div className="profile-card-header">
              <div className="profile-initials">C</div>
              <div className="profile-basic-info">
                <h3 className="profile-name">Company Information</h3>
                <p className="profile-company-label">
                  Details for {userCompany.companyName}
                </p>
              </div>
            </div>
            <div className="profile-card-body">
              <div className="profile-detail-row">
                <span className="detail-label">Company Code:</span>
                <span className="detail-value">{userCompany.companyCode}</span>
              </div>
              <div className="profile-detail-row">
                <span className="detail-label">Company Name:</span>
                <span className="detail-value">{userCompany.companyName}</span>
              </div>
              <div className="profile-detail-row">
                <span className="detail-label">Web Service URL:</span>
                <span className="detail-value">
                  {userCompany.webServiceUrl}
                </span>
              </div>
              {/* <div className="profile-detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">{userCompany.status}</span>
              </div> */}
              <div className="profile-detail-row">
                <span className="detail-label">Remote User:</span>
                <span className="detail-value">{userCompany.remoteUser}</span>
              </div>
              <div className="profile-detail-row">
                <span className="detail-label">Remote Password:</span>
                <span className="detail-value">
                  *{/* {userCompany.remotePassword} */}
                </span>
              </div>
              <div className="profile-detail-row">
                <span className="detail-label">Last Sync:</span>
                <span className="detail-value">
                  {new Date(userCompany.lastSync).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="profile-modal-overlay">
          <div className="profile-modal-content">
            <h3>Edit Profile</h3>
            {error && <div className="profile-error-message">{error}</div>}
            <form onSubmit={handleEditSubmit}>
              <div className="profile-form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="profile-form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />
              </div>
              <div className="profile-form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  required
                />
              </div>
              <div className="profile-form-group">
                <label htmlFor="password">New Password (optional)</label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
              </div>
              <div className="profile-form-group">
                <label htmlFor="companyCode">Company Code</label>
                <input
                  type="text"
                  id="companyCode"
                  value={formData.companyCode}
                  disabled
                  className="profile-input-disabled"
                  required
                />
              </div>
              <div className="profile-modal-actions">
                <button
                  type="button"
                  className="profile-glass-btn profile-cancel-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="profile-glass-btn profile-submit-btn"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isConfirmOpen && (
        <div className="profile-modal-overlay">
          <div className="profile-modal-content">
            <h3>Confirm Sync</h3>
            <p>
              Are you sure you want to{" "}
              {confirmAction === "SendData"
                ? "send data"
                : `sync ${confirmAction}`}
              ?
            </p>
            <div className="profile-modal-actions">
              <button
                type="button"
                className="profile-glass-btn profile-cancel-btn"
                onClick={() => setIsConfirmOpen(false)}
                disabled={isSyncing}
              >
                No
              </button>
              <button
                type="button"
                className="profile-glass-btn profile-submit-btn"
                onClick={() => handleSync(confirmAction)}
                disabled={isSyncing}
              >
                {isSyncing ? "Syncing..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
